import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TID } from "@/lib/config";
import { Menu, X } from "lucide-react";
import { useConsent } from "@/lib/consent";
import { useFeatures } from "@/lib/features";

// Shop is intentionally NOT listed here: it lives on the shop.gefion-evolution.it
// subdomain and must not be reachable from the corporate site navigation.
const ALL_LINKS = [
  { to: "/", label: "Home", tid: TID.navHome },
  { to: "/#divisioni", label: "Divisioni", tid: TID.navDivisioni, hash: true },
  { to: "/taglio-laser", label: "Taglio Laser", tid: TID.navLaser },
  { to: "/assistenza", label: "Assistenza", tid: "nav-assistenza" },
  { to: "/blog", label: "Blog", tid: "nav-blog", flag: "blog_enabled" },
  { to: "/contatti", label: "Contatti", tid: TID.navContatti },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { features } = useFeatures();
  const links = ALL_LINKS.filter((l) => !l.flag || features[l.flag] !== false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname, loc.hash]);

  const handleHash = (e, to) => {
    if (loc.pathname === "/" && to.startsWith("/#")) {
      e.preventDefault();
      const id = to.split("#")[1];
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 ${
          scrolled ? "bg-[#050505]/80 backdrop-blur-xl border-b border-[#1a1a1a]" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid={TID.logo} className="flex items-center gap-3 group">
            <img src="/assets/logo/gefion.png" alt="Gefion Evolution" className="h-9 md:h-10 w-auto object-contain brightness-0 invert" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) =>
              l.hash ? (
                <a key={l.to} href={l.to} data-testid={l.tid} onClick={(e) => handleHash(e, l.to)}
                  className="font-mono-tech text-[11px] text-white/70 hover:text-[#ff4b00]">
                  {l.label}
                </a>
              ) : (
                <NavLink key={l.to} to={l.to} data-testid={l.tid}
                  className={({ isActive }) =>
                    `font-mono-tech text-[11px] hover:text-[#ff4b00] ${isActive ? "text-[#ff4b00]" : "text-white/70"}`}>
                  {l.label}
                </NavLink>
              )
            )}
            <NavLink to="/area/login" data-testid="nav-area" className="font-mono-tech text-[11px] text-white/60 hover:text-[#ff4b00]">
              Area cliente
            </NavLink>
            <NavLink to="/admin" data-testid={TID.navAdmin} className="font-mono-tech text-[11px] text-white/40 hover:text-white/80 border border-[#222] px-3 py-1.5">
              Area riservata
            </NavLink>
          </nav>
          <button data-testid={TID.mobileMenuBtn} aria-label="Menu" className="lg:hidden p-3 border border-[#333] bg-[#0a0a0a] flex items-center gap-2 font-mono-tech text-[10px]" onClick={() => setOpen((v) => !v)}>
            {open ? <X size={16} /> : <Menu size={16} />} {open ? "CHIUDI" : "MENU"}
          </button>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#050505]/95 backdrop-blur-xl lg:hidden pt-[72px]">
            <div className="flex flex-col p-8 gap-6">
              {links.map((l) => (
                <a key={l.to} href={l.to} onClick={(e) => { handleHash(e, l.to); }}
                  className="font-display text-4xl text-white/90 hover:text-[#ff4b00]">
                  {l.label}
                </a>
              ))}
              <Link to="/admin" className="font-mono-tech text-[11px] text-white/50 mt-8 border border-[#222] px-3 py-2 self-start">Area riservata</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const Footer = () => {
  const { open: openCookiePrefs } = useConsent();
  return (
  <footer className="relative border-t border-[#1a1a1a] mt-24">
    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <img src="/assets/logo/gefion.png" alt="Gefion Evolution" className="h-12 w-auto object-contain mb-6 brightness-0 invert" />
        <div className="font-mono-tech text-[10px] text-white/50 mb-3">◐ Gefion S.r.l.</div>
        <div className="font-display text-3xl md:text-4xl leading-[0.95]">Il posto dove le idee<br/>diventano progetti.</div>
      </div>
      <div className="md:col-span-3">
        <div className="font-mono-tech text-[10px] text-white/50 mb-4">Divisioni</div>
        <ul className="space-y-2 text-white/70 text-sm">
          <li><Link to="/divisione/elettrico" className="hover:text-[#ff4b00]">Impianti Elettrici</Link></li>
          <li><Link to="/divisione/automazioni" className="hover:text-[#ff4b00]">Automazioni</Link></li>
          <li><Link to="/divisione/fotovoltaico" className="hover:text-[#ff4b00]">Fotovoltaico</Link></li>
          <li><Link to="/divisione/antiincendio" className="hover:text-[#ff4b00]">Antincendio</Link></li>
          <li><Link to="/taglio-laser" className="hover:text-[#ff4b00]">Taglio Laser</Link></li>
        </ul>
      </div>
      <div className="md:col-span-4">
        <div className="font-mono-tech text-[10px] text-white/50 mb-4">Contatti</div>
        <div className="space-y-1 text-sm text-white/70">
          <div>Gefion S.r.l.</div>
          <a href="https://maps.google.com/?q=Via+1%C2%B0+Maggio+6+Budoia+PN" target="_blank" rel="noreferrer" className="hover:text-[#ff4b00] block">Via 1° Maggio, 6</a>
          <a href="https://maps.google.com/?q=Via+1%C2%B0+Maggio+6+Budoia+PN" target="_blank" rel="noreferrer" className="hover:text-[#ff4b00] block">33070 Budoia (PN) — Italia</a>
          <div className="pt-3"><a href="mailto:info@gefion-evolution.it" className="hover:text-[#ff4b00]">info@gefion-evolution.it</a></div>
        </div>
      </div>
    </div>
    <div className="border-t border-[#1a1a1a]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 font-mono-tech text-[10px] text-white/40">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span>© {new Date().getFullYear()} Gefion S.r.l.</span>
          <span>P.IVA 01675990939</span>
          <span>PEC gefionsrl@legalmail.it</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          <Link to="/privacy" className="hover:text-[#ff4b00]" data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/cookie-policy" className="hover:text-[#ff4b00]" data-testid="footer-cookie">Cookie Policy</Link>
          <button onClick={openCookiePrefs} className="hover:text-[#ff4b00] cursor-pointer" data-testid="footer-cookie-prefs">Preferenze cookie</button>
          <Link to="/termini" className="hover:text-[#ff4b00]" data-testid="footer-terms">Termini di Servizio</Link>
        </div>
      </div>
    </div>
  </footer>
  );
};

export const Noise = () => <div className="grain" aria-hidden />;
